import React, { createContext, useContext, useState, useEffect } from 'react';
import { ProductUpdatesService } from '../../services/ProductUpdatesService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ProductUpdatesContext = createContext();

export const useProductUpdates = () => {
  const context = useContext(ProductUpdatesContext);
  if (!context) {
    throw new Error('useProductUpdates must be used within a ProductUpdatesProvider');
  }
  return context;
};

export const ProductUpdatesProvider = ({ children }) => {
  const { t } = useTranslation();
  const [updates, setUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUpdates = async () => {
    if (hasFetched) return; // Skip if already fetched
    
    try {
      setIsLoading(true);
      const data = await ProductUpdatesService.fetchUpdates();
      // Sort updates by _id (timestamp) in descending order (newer first)
      const sortedData = [...data].sort((a, b) => Number(b._id) - Number(a._id));
      setUpdates(sortedData);
      setHasFetched(true);
    } catch (error) {
      toast.error(t('errorFetchingUpdates'));
    } finally {
      setIsLoading(false);
    }
  };

  const createUpdate = async (updateData) => {
    try {
      const dataToSend = {
        _id: Date.now(),
        title: updateData.title,
        description: updateData.description || '',
        type: updateData.type || 'live'
      };
      const data = await ProductUpdatesService.createUpdate(dataToSend);
      
      const newUpdate = {
        ...dataToSend,
        ...data
      };
      
      // Add new update and sort the array
      setUpdates((prev) => [newUpdate, ...prev]);
      toast.success(t('updateCreatedSuccessfully'));
      return newUpdate;
    } catch (error) {
      toast.error(t('errorCreatingUpdate'));
      throw error;
    }
  };

  const updateUpdate = async (_id, updateData) => {
    try {
      const data = await ProductUpdatesService.updateUpdate(_id, updateData);
      setUpdates((prev) =>
        prev.map((update) => (update._id === _id ? { ...update, ...updateData } : update))
      );
      toast.success(t('updateModifiedSuccessfully'));
      return data;
    } catch (error) {
      toast.error(t('errorModifyingUpdate'));
      throw error;
    }
  };

  const deleteUpdate = async (_id) => {
    try {
      await ProductUpdatesService.deleteUpdate(_id);
      setUpdates((prev) => prev.filter((update) => update._id !== _id));
      toast.success(t('updateDeletedSuccessfully'));
    } catch (error) {
      toast.error(t('errorDeletingUpdate'));
      throw error;
    }
  };

  const value = {
    updates,
    isLoading,
    createUpdate,
    updateUpdate,
    deleteUpdate,
    refreshUpdates: fetchUpdates,
    hasFetched
  };

  return (
    <ProductUpdatesContext.Provider value={value}>
      {children}
    </ProductUpdatesContext.Provider>
  );
}; 